import {
  useCallback,
  useEffect,
  useState,
  type DependencyList,
  type Dispatch,
  type SetStateAction,
} from "react";
import { ApiError } from "../api/client";

export interface AsyncData<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload(): Promise<void>;
  setData: Dispatch<SetStateAction<T | null>>;
}

export function useAsyncData<T>(
  loader: () => Promise<T>,
  dependencies: DependencyList,
): AsyncData<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await loader());
      setError(null);
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.status === 401) {
        window.dispatchEvent(new CustomEvent("swipecut:session-expired"));
      }
      setError(requestError instanceof Error ? requestError.message : "The request failed.");
    } finally {
      setLoading(false);
    }
    // The caller controls loader stability through the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const refresh = () => void reload();
    window.addEventListener("swipecut:refresh", refresh);
    return () => window.removeEventListener("swipecut:refresh", refresh);
  }, [reload]);

  return { data, loading, error, reload, setData };
}
